import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Sparkles, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from '../components/PageLoader';
import { InlineError } from '../components/InlineError';
import LinkedBadge from '../components/LinkedBadge';
import SourceLink from '../components/SourceLink';
import type { GeneratedDocument } from '../lib/types';
import { generateLabel } from '../lib/generation';
import { useAgentCard } from '../components/AgentDock';


const GROUPS: { kind: GeneratedDocument['kind']; title: string; intro: string }[] = [
    {
        kind: 'data',
        title: 'Reports',
        intro: 'Figures and lists built from your record and kept up to date as it changes. Some include commentary written by Navaris.',
    },
    {
        kind: 'draft',
        title: 'Drafts',
        intro: "Documents Navaris writes from your record, for you to review and complete. Figures come from the record; anything it could not trace, and anything the record does not yet say, is highlighted. Drafted again only when you ask.",
    },
];

function DocCard({ doc, onGenerate }: {
    doc: GeneratedDocument;
    onGenerate: (doc: GeneratedDocument) => void;
}) {
    const busy = !!doc.active_job;
    return (
        <div className="border border-softgrey rounded-xl p-4">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-graphite">{doc.name}</h4>
                        {doc.linked && <LinkedBadge />}
                    </div>
                    {doc.description && <p className="text-[13px] text-coolslate mt-1">{doc.description}</p>}
                    {doc.byline && (
                        <p className="text-[12px] text-coolslate/80 mt-1">
                            {doc.kind === 'draft' ? `Written by ${doc.byline}` : `Commentary by ${doc.byline}`}
                        </p>
                    )}

                    <div className="mt-2 text-[12px] text-coolslate">
                        {doc.sources.length > 0 ? (
                            <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                <span className="text-coolslate/70">Built from</span>
                                {doc.sources.map((s, i) => (
                                    <span key={s.id}>
                                        <SourceLink source={s}>{s.name ?? s.id}</SourceLink>
                                        {i < doc.sources.length - 1 ? ',' : ''}
                                    </span>
                                ))}
                            </span>
                        ) : (
                            <span className="text-coolslate/70">No data yet</span>
                        )}
                    </div>

                    {!doc.ready && doc.missing.length > 0 && (
                        <p className="mt-1.5 text-[12px] text-state-error">
                            Needs data for: {doc.missing.join(', ')}
                        </p>
                    )}
                </div>

                <div className="shrink-0 flex items-center gap-2">
                    {doc.file_id && (
                        <Link
                            to={`/files/${doc.file_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-graphite border border-softgrey rounded-lg hover:bg-offwhite transition-colors"
                        >
                            <FileText className="w-3.5 h-3.5" /> View
                        </Link>
                    )}
                    <button
                        onClick={() => onGenerate(doc)}
                        disabled={!doc.ready || busy}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!doc.ready ? 'Not enough data yet' : undefined}
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        {busy ? 'Generating…' : generateLabel(doc)}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function GeneratedDocumentsPage() {
    const { authFetch } = useAuth();
    const [docs, setDocs] = useState<GeneratedDocument[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const { startChat } = useAgentCard();

    const load = useCallback((silent = false) => {
        if (!silent) setDocs(null);
        return authFetch('/workspace/generated')
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load generated documents')))
            .then((data: GeneratedDocument[]) => setDocs(data))
            .catch(err => { setError(err.message); setDocs([]); });
    }, [authFetch]);

    useEffect(() => { load(); }, [load]);

    // Poll while anything is generating so the View link + Linked badge land.
    useEffect(() => {
        const anyActive = (docs ?? []).some(d => d.active_job);
        if (!anyActive) return;
        const t = setInterval(() => load(true), 4000);
        return () => clearInterval(t);
    }, [docs, load]);

    const generate = (doc: GeneratedDocument) => {
        startChat(doc.skill ?? undefined, {
            ask: `Generate the "${doc.name}" document (document type ${doc.definition_id}).`,
        });
    };

    if (docs === null) return <PageLoader label="Loading generated documents..." />;

    return (
        <div>
            <div className="pb-6 mb-6 border-b border-softgrey">
                <h3 className="text-lg font-semibold">Generated documents</h3>
                <p className="text-sm text-coolslate mt-1">
                    Documents built from your company's knowledge. Each one
                    draws on whatever sources and documents have contributed to it.
                </p>
            </div>

            {error && <InlineError message={error} />}

            {docs.length === 0 ? (
                <p className="navaris-empty">No generatable documents yet.</p>
            ) : (
                GROUPS.map(group => {
                    const members = docs.filter(d => d.kind === group.kind)
                        .sort((a, b) => a.name.localeCompare(b.name));
                    if (members.length === 0) return null;
                    return (
                        <section key={group.kind} className="mb-10">
                            <h4 className="navaris-h3">{group.title}</h4>
                            <p className="text-[13px] text-coolslate mt-1 mb-4">{group.intro}</p>
                            <div className="space-y-3">
                                {members.map(doc => (
                                    <DocCard
                                        key={doc.definition_id}
                                        doc={doc}
                                        onGenerate={generate}
                                    />
                                ))}
                            </div>
                        </section>
                    );
                })
            )}
        </div>
    );
}
